import { useState } from "react";
import { KeyRound, ShieldAlert, Loader2, Eye, EyeOff, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuthStore } from "../store/authStore";

/**
 * First-login enforced password change modal.
 *
 * Rendered by `AppShell` whenever the currently-authenticated user has
 * `must_change_password === true`. The modal is intentionally NOT
 * dismissible (no ESC / backdrop close, no cancel button) — the user
 * cannot reach any tenant-scoped page until they rotate their password.
 *
 * Backend contract: `POST /auth/change-password` with
 * `{new_password}` (no `old_password` because the DB flag lets the
 * server skip that check). On 200 we call `fetchMe()` so the auth
 * store picks up `must_change_password: false` and the modal unmounts.
 */
export default function MustChangePasswordDialog() {
  const { user, fetchMe } = useAuthStore();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user || !user.must_change_password) return null;

  const strength = strengthLabel(pw1);
  const canSubmit =
    pw1.length >= 8 && pw1 === pw2 && !busy;

  const submit = async () => {
    if (pw1.length < 8) {
      toast.error("Şifre en az 8 karakter olmalı");
      return;
    }
    if (pw1 !== pw2) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: pw1 });
      toast.success("Şifren değiştirildi");
      // Refresh the auth store so `must_change_password` flips to false
      // and this dialog unmounts on the next render.
      await fetchMe();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Şifre değiştirilemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        // Prevent Radix's outside-click / esc dismiss.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        // Hide the built-in close button; the only exit is a successful save.
        // shadcn's DialogPrimitive.Close renders an absolute-positioned
        // direct-child button (see components/ui/dialog.jsx).
        className="[&>button.absolute]:hidden"
        data-testid="must-change-password-dialog"
      >
        <DialogHeader>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-amber-100">
            <ShieldAlert className="h-5 w-5 text-amber-700" />
          </div>
          <DialogTitle>Yeni şifreni belirle</DialogTitle>
          <DialogDescription>
            Hoş geldin{user.name ? `, ${user.name.split(" ")[0]}` : ""}!
            Yönetici hesabına geçici bir şifre atadı. Devam etmek için kendi
            şifreni belirlemelisin.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-xs">
              <KeyRound className="h-3.5 w-3.5 text-zinc-500" /> Yeni Şifre
            </Label>
            <div className="relative">
              <Input
                type={show ? "text" : "password"}
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                disabled={busy}
                placeholder="Minimum 8 karakter"
                autoFocus
                data-testid="must-change-pw-new"
                className="pr-10 font-mono"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                aria-label={show ? "Şifreyi gizle" : "Şifreyi göster"}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {pw1 && (
              <div className="flex items-center gap-2 text-[11px]">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-200">
                  <div
                    className={`h-full transition-all ${strength.bar}`}
                    style={{ width: strength.width }}
                  />
                </div>
                <span className={`font-medium ${strength.text}`}>{strength.label}</span>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-xs">
              <KeyRound className="h-3.5 w-3.5 text-zinc-500" /> Şifre (tekrar)
            </Label>
            <Input
              type={show ? "text" : "password"}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              disabled={busy}
              placeholder="Yeni şifreyi tekrar gir"
              data-testid="must-change-pw-confirm"
              className="font-mono"
            />
            {pw2 && pw1 !== pw2 && (
              <p className="text-[11px] text-rose-600">Şifreler eşleşmiyor</p>
            )}
            {pw2 && pw1 === pw2 && pw1.length >= 8 && (
              <p className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                <Check className="h-3 w-3" /> Şifreler eşleşiyor
              </p>
            )}
          </div>
        </div>

        <Button
          onClick={submit}
          disabled={!canSubmit}
          data-testid="must-change-pw-submit"
          className="w-full bg-violet-600 text-white hover:bg-violet-700"
        >
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Kaydediliyor…
            </>
          ) : (
            "Şifreyi Belirle ve Devam Et"
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function strengthLabel(pw) {
  // Cheap heuristic — length + character-class diversity. Meant to
  // guide the user, not to enforce anything (backend already floors
  // at 8 chars).
  if (pw.length < 8) return { label: "Çok kısa", width: "20%", bar: "bg-rose-400", text: "text-rose-600" };
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((rx) => rx.test(pw)).length;
  if (classes <= 2) return { label: "Zayıf", width: "40%", bar: "bg-amber-400", text: "text-amber-700" };
  if (classes === 3) return { label: "Orta", width: "70%", bar: "bg-sky-500", text: "text-sky-700" };
  return { label: "Güçlü", width: "100%", bar: "bg-emerald-500", text: "text-emerald-700" };
}
